import { useEffect, useState } from "react";
import { fetchFromApi, getStorageUrl } from "../lib/api";

interface PlacementWithIllustration<T = { link?: string }> {
    id: number;
    image_id: string;
    section_id: number;
    sort_order: number;
    additional_config: T;
    illustration: {
        id: string;
        title: string;
        storage_path: string;
    };
    storage_path: string; // Flattened in new API
    title: string;
    category: string;
}

interface ImageOptions {
    width?: number;
    height?: number;
    quality?: number;
}

interface UseImagePlacementResult<T> {
    imageUrls: string[];
    isLoading: boolean;
    error: string | null;
    placements: PlacementWithIllustration<T>[];
}

export const useImagePlacement = <T = { link?: string }>(
    section: number | string,
    _options?: ImageOptions,
): UseImagePlacementResult<T> => {
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [placements, setPlacements] = useState<
        PlacementWithIllustration<T>[]
    >([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchImagePlacements = async () => {
            try {
                setIsLoading(true);
                const data = await fetchFromApi(`/layout/${section}`) as any[];

                if (!data || data.length === 0) {
                    console.log(`No placements found for section ${section}`);
                    setIsLoading(false);
                    return;
                }

                // New API already returns flattened structure, but we can wrap it if needed for compatibility
                const formattedPlacements = data.map(item => ({
                    ...item,
                    additional_config: typeof item.additional_config === 'string' 
                        ? JSON.parse(item.additional_config) 
                        : item.additional_config,
                    illustration: {
                        id: item.image_id,
                        title: item.title,
                        storage_path: item.storage_path
                    }
                }));

                setPlacements(formattedPlacements);

                const urls = data.map((item) => {
                    // Try to use illustrations bucket first, but layout items are often in features
                    return getStorageUrl("features", item.storage_path);
                });
                setImageUrls(urls);
            } catch (err) {
                console.error("画像の取得に失敗しました:", err);
                setError("画像の取得に失敗しました");
            } finally {
                setIsLoading(false);
            }
        };

        fetchImagePlacements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [section]);

    return { imageUrls, isLoading, error, placements };
};
